import {
  IFileDetails,
  IFolderDetails,
  IWorkspace,
  IWorkspaceFileItem,
  IWorkspaceFolderItem,
} from '@dataTypes/folder.types'
import moment from 'moment'

const MOCK_ROOT_FOLDER_ID = 'mock-root-folder'

const PREVIEW_PDF = '/sample-consent.pdf'
const PREVIEW_PANORAMIC =
  'https://upload.wikimedia.org/wikipedia/commons/a/ac/Dental_Panorama_X-ray.jpg'
const PREVIEW_FULL_MOUTH =
  'https://upload.wikimedia.org/wikipedia/commons/4/4c/X-ray_of_all_32_human_teeth.jpg'
const PREVIEW_PERIAPICAL =
  'https://upload.wikimedia.org/wikipedia/commons/5/50/Abscessed_tooth_periapical_radiograph.jpg'
const PREVIEW_SMILE =
  'https://upload.wikimedia.org/wikipedia/commons/7/7e/Smile_Makeover_of_a_Dental_Phobia_patient.jpg'
const PREVIEW_CARD = '/clinic.png'

type MockFileMeta = {
  previewUrl?: string
  uploader?: string
  claimIds?: string[]
  attachedToForms?: boolean
}

const mockFileMeta: Record<string, MockFileMeta> = {}

const MIME_BY_UI_TYPE: Record<IWorkspaceFileItem['ui_type'], string> = {
  PDF: 'application/pdf',
  Image: 'image/jpeg',
  Document:
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'X-Ray': 'application/dicom',
  Other: 'application/octet-stream',
}

const mockFile = (
  id: string,
  name: string,
  size: number,
  daysAgo: number,
  folderId: string | null = null,
  uiType: IWorkspaceFileItem['ui_type'] = 'Document',
  options: Partial<IWorkspaceFileItem> & MockFileMeta = {},
): IWorkspaceFileItem => {
  const { previewUrl, uploader, claimIds, attachedToForms, ...overrides } =
    options
  mockFileMeta[id] = { previewUrl, uploader, claimIds, attachedToForms }
  return {
    file_id: id,
    folder_id: folderId,
    file_name: name,
    original_name: name,
    file_type: uiType === 'X-Ray' ? 'dicom' : uiType.toLowerCase(),
    ui_type: uiType,
    mime_type: MIME_BY_UI_TYPE[uiType],
    file_size: size,
    is_favorite: false,
    download_count: 0,
    last_accessed: null,
    date_time_created_at: moment().subtract(daysAgo, 'days').toISOString(),
    created_by: 'mock-user',
    study_uid: null,
    series_uid: null,
    sop_uid: null,
    ...overrides,
  }
}

const mockFolder = (
  id: string,
  name: string,
  fileCount: number,
  folderCount: number,
  size: number,
  daysAgo: number,
  options: Partial<IWorkspaceFolderItem> = {},
): IWorkspaceFolderItem => ({
  folder_id: id,
  folder_name: name,
  parent_folder_id: null,
  path: `/${name}`,
  total_files: fileCount,
  total_folders: folderCount,
  total_items: fileCount + folderCount,
  folder_size: size,
  is_default: false,
  date_time_updated_at: moment().subtract(daysAgo, 'days').toISOString(),
  ...options,
})

const createWorkspace = (
  patientId: string,
  folderId: string | null,
  breadcrumbs: IWorkspace['breadcrumbs'],
  folders: IWorkspaceFolderItem[],
  files: IWorkspaceFileItem[],
): IWorkspace => ({
  scope: {
    patient_id: patientId,
    folder_id: folderId,
    root_folder_id: MOCK_ROOT_FOLDER_ID,
    is_root: !folderId || folderId === 'root',
  },
  breadcrumbs,
  folders,
  files,
  pagination: {
    page: 1,
    limit: 100,
    total_items: folders.length + files.length,
    total_pages: 1,
  },
})

const ROOT_CRUMB = {
  folder_id: MOCK_ROOT_FOLDER_ID,
  folder_name: 'Files',
  path: '/',
}

const crumb = (
  folderId: string,
  folderName: string,
  path = `/${folderName}`,
): IWorkspace['breadcrumbs'][number] => ({
  folder_id: folderId,
  folder_name: folderName,
  path,
})

// ---------------------------------------------------------------------------
// mock-patient-1 — John Mark Kennedy Doe
// ---------------------------------------------------------------------------

const P1 = 'mock-patient-1'
const DR_SMITH = 'Dr. John Michael Smith'
const HYG_JOHNSON = 'Sarah Elizabeth Johnson'
const FRONT_DESK = 'Front Desk'

const P1_XRAY = 'mock-folder-1-xray'
const P1_FORMS = 'mock-folder-1-forms'
const P1_CLAIMS = 'mock-folder-1-claims'
const P1_IMAGES = 'mock-folder-1-images'
const P1_CLINICAL = 'mock-folder-1-clinical'
const P1_CLINICAL_2025 = 'mock-folder-1-clinical-2025'
const P1_INSURANCE = 'mock-folder-1-insurance'

const p1XrayFiles = [
  mockFile(
    'mock-file-1-pano',
    'Panoramic_Radiograph_2026.dcm',
    45_200_000,
    6,
    P1_XRAY,
    'X-Ray',
    {
      is_favorite: true,
      previewUrl: PREVIEW_PANORAMIC,
      uploader: DR_SMITH,
      study_uid:
        '1.3.6.1.4.1.44316.6.102.1.2023091384336494.746252101381252750643',
      series_uid:
        '1.3.6.1.4.1.44316.6.102.2.2023091384336494.575117162211064181910',
    },
  ),
  mockFile(
    'mock-file-1-cbct',
    'CBCT_Full_Mouth.dcm',
    7_340_032,
    6,
    P1_XRAY,
    'X-Ray',
    {
      previewUrl: PREVIEW_FULL_MOUTH,
      uploader: DR_SMITH,
      study_uid: '1.2.826.0.1.3680043.8.1055.1775107995355.17324',
      series_uid: '1.2.826.0.1.3680043.8.1055.1775107995355.141406',
    },
  ),
  mockFile(
    'mock-file-1-bitewing-left',
    'Bitewing_Left.jpg',
    1_572_864,
    20,
    P1_XRAY,
    'Image',
    {
      previewUrl: PREVIEW_FULL_MOUTH,
      uploader: HYG_JOHNSON,
      claimIds: ['C12345'],
    },
  ),
  mockFile(
    'mock-file-1-bitewing-right',
    'Bitewing_Right.jpg',
    1_498_112,
    20,
    P1_XRAY,
    'Image',
    { previewUrl: PREVIEW_FULL_MOUTH, uploader: HYG_JOHNSON },
  ),
  mockFile(
    'mock-file-1-pa-14',
    'Periapical_Tooth_14.png',
    845_000,
    34,
    P1_XRAY,
    'Image',
    {
      previewUrl: PREVIEW_PERIAPICAL,
      uploader: DR_SMITH,
      claimIds: ['C12345'],
    },
  ),
]

const p1FormsFiles = [
  mockFile(
    'mock-file-1-tx-letter',
    'Treatment_Plan_Letter.pdf',
    512_000,
    5,
    P1_FORMS,
    'PDF',
    {
      is_favorite: true,
      previewUrl: PREVIEW_PDF,
      uploader: DR_SMITH,
      attachedToForms: true,
    },
  ),
  mockFile(
    'mock-file-1-referral',
    'Referral_Letter_Oral_Surgeon.pdf',
    204_800,
    12,
    P1_FORMS,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: DR_SMITH, attachedToForms: true },
  ),
  mockFile(
    'mock-file-1-financial',
    'Financial_Agreement_Signed.pdf',
    176_128,
    40,
    P1_FORMS,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: FRONT_DESK, attachedToForms: true },
  ),
  mockFile(
    'mock-file-1-post-op',
    'Post_Op_Instructions.docx',
    48_640,
    5,
    P1_FORMS,
    'Document',
    { uploader: HYG_JOHNSON },
  ),
]

const p1ClaimsFiles = [
  mockFile(
    'mock-file-1-claim-narrative',
    'Claim_C12345_Narrative.pdf',
    98_304,
    18,
    P1_CLAIMS,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: FRONT_DESK, claimIds: ['C12345'] },
  ),
  mockFile(
    'mock-file-1-eob',
    'EOB_Blue_Cross_September.pdf',
    143_360,
    9,
    P1_CLAIMS,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: FRONT_DESK, claimIds: ['C12345'] },
  ),
]

const p1ImagesFiles = [
  mockFile(
    'mock-file-1-smile-before',
    'Smile_Before_Whitening.jpg',
    2_097_152,
    60,
    P1_IMAGES,
    'Image',
    { previewUrl: PREVIEW_SMILE, uploader: HYG_JOHNSON },
  ),
  mockFile(
    'mock-file-1-intraoral-upper',
    'Intraoral_Upper_Arch.jpg',
    1_835_008,
    6,
    P1_IMAGES,
    'Image',
    { is_favorite: true, previewUrl: PREVIEW_SMILE, uploader: HYG_JOHNSON },
  ),
  mockFile(
    'mock-file-1-intraoral-lower',
    'Intraoral_Lower_Arch.jpg',
    1_769_472,
    6,
    P1_IMAGES,
    'Image',
    { previewUrl: PREVIEW_SMILE, uploader: HYG_JOHNSON },
  ),
]

const p1Clinical2025Files = [
  mockFile(
    'mock-file-1-visit-nov',
    'Visit_Summary_November_2025.pdf',
    230_400,
    320,
    P1_CLINICAL_2025,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: DR_SMITH },
  ),
  mockFile(
    'mock-file-1-perio-chart',
    'Perio_Chart_2025.pdf',
    310_272,
    330,
    P1_CLINICAL_2025,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: HYG_JOHNSON },
  ),
]

const p1ClinicalFiles = [
  mockFile(
    'mock-file-1-treatment',
    'Treatment_Plan_2026.pdf',
    512_000,
    10,
    P1_CLINICAL,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: DR_SMITH, claimIds: ['C12345'] },
  ),
  mockFile(
    'mock-file-1-history',
    'Medical_History.docx',
    98_304,
    60,
    P1_CLINICAL,
    'Document',
    { uploader: FRONT_DESK, attachedToForms: true },
  ),
  mockFile(
    'mock-file-1-allergies',
    'Allergy_List_Penicillin_Latex_Shellfish.pdf',
    65_536,
    60,
    P1_CLINICAL,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: FRONT_DESK },
  ),
]

const p1InsuranceFiles = [
  mockFile(
    'mock-file-1-ins-card-front',
    'Insurance_Card_Front.png',
    320_000,
    30,
    P1_INSURANCE,
    'Image',
    { previewUrl: PREVIEW_CARD, uploader: FRONT_DESK },
  ),
  mockFile(
    'mock-file-1-ins-card-back',
    'Insurance_Card_Back.png',
    298_000,
    30,
    P1_INSURANCE,
    'Image',
    { previewUrl: PREVIEW_CARD, uploader: FRONT_DESK },
  ),
  mockFile(
    'mock-file-1-ins-verification',
    'Blue_Cross_Eligibility_Verification.pdf',
    122_880,
    28,
    P1_INSURANCE,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: FRONT_DESK },
  ),
]

const p1RootFiles = [
  mockFile(
    'mock-file-1-intake',
    'Patient_Intake_Form.pdf',
    245_760,
    21,
    null,
    'PDF',
    {
      is_favorite: true,
      previewUrl: PREVIEW_PDF,
      uploader: FRONT_DESK,
      attachedToForms: true,
    },
  ),
  mockFile(
    'mock-file-1-consent',
    'HIPAA_Consent_Signed.pdf',
    189_440,
    45,
    null,
    'PDF',
    { previewUrl: PREVIEW_PDF, uploader: FRONT_DESK, claimIds: ['C12345'] },
  ),
  mockFile(
    'mock-file-1-photo-id',
    'Photo_ID_Drivers_License.jpg',
    612_352,
    45,
    null,
    'Image',
    { previewUrl: PREVIEW_CARD, uploader: FRONT_DESK },
  ),
  mockFile(
    'mock-file-1-welcome',
    'Welcome_Letter.docx',
    36_864,
    45,
    null,
    'Document',
    { uploader: FRONT_DESK },
  ),
]

const sumSize = (files: IWorkspaceFileItem[]): number =>
  files.reduce((total, file) => total + file.file_size, 0)

const p1Clinical2025Folder = mockFolder(
  P1_CLINICAL_2025,
  '2025 Visits',
  p1Clinical2025Files.length,
  0,
  sumSize(p1Clinical2025Files),
  320,
  { parent_folder_id: P1_CLINICAL, path: '/Clinical records/2025 Visits' },
)

const p1RootFolders = [
  mockFolder(
    P1_XRAY,
    'X-Ray Files',
    p1XrayFiles.length,
    0,
    sumSize(p1XrayFiles),
    6,
    {
      is_default: true,
    },
  ),
  mockFolder(
    P1_FORMS,
    'Forms and Letters',
    p1FormsFiles.length,
    0,
    sumSize(p1FormsFiles),
    5,
    { is_default: true },
  ),
  mockFolder(
    P1_CLAIMS,
    'Claims',
    p1ClaimsFiles.length,
    0,
    sumSize(p1ClaimsFiles),
    9,
    {
      is_default: true,
    },
  ),
  mockFolder(
    P1_IMAGES,
    'Images',
    p1ImagesFiles.length,
    0,
    sumSize(p1ImagesFiles),
    6,
    {
      is_default: true,
    },
  ),
  mockFolder(
    P1_CLINICAL,
    'Clinical records',
    p1ClinicalFiles.length,
    1,
    sumSize(p1ClinicalFiles) + sumSize(p1Clinical2025Files),
    10,
  ),
  mockFolder(
    P1_INSURANCE,
    'Insurance documents',
    p1InsuranceFiles.length,
    0,
    sumSize(p1InsuranceFiles),
    28,
  ),
]

const p1FolderWorkspace = (
  folderId: string,
  breadcrumbs: IWorkspace['breadcrumbs'],
  files: IWorkspaceFileItem[],
  folders: IWorkspaceFolderItem[] = [],
): IWorkspace => createWorkspace(P1, folderId, breadcrumbs, folders, files)

// ---------------------------------------------------------------------------
// mock-patient-2 — Jane Marie Smith
// ---------------------------------------------------------------------------

const mockPatient2Root = createWorkspace(
  'mock-patient-2',
  null,
  [ROOT_CRUMB],
  [
    mockFolder('mock-folder-2-clinical', 'Clinical records', 1, 0, 890_000, 5),
    mockFolder('mock-folder-2-lab', 'Lab results', 2, 0, 1_100_000, 12),
  ],
  [
    mockFile(
      'mock-file-2-intake',
      'New_Patient_Form.pdf',
      210_944,
      3,
      null,
      'PDF',
      { previewUrl: PREVIEW_PDF },
    ),
    mockFile(
      'mock-file-2-referral',
      'Specialist_Referral.pdf',
      156_672,
      18,
      null,
      'PDF',
      { previewUrl: PREVIEW_PDF },
    ),
    mockFile(
      'mock-file-2-photo',
      'Smile_Photo.jpg',
      874_496,
      2,
      null,
      'Image',
      {
        previewUrl: PREVIEW_SMILE,
      },
    ),
  ],
)

const mockPatient2Clinical = createWorkspace(
  'mock-patient-2',
  'mock-folder-2-clinical',
  [ROOT_CRUMB, crumb('mock-folder-2-clinical', 'Clinical records')],
  [],
  [
    mockFile(
      'mock-file-2-chart',
      'Chart_Notes_March.pdf',
      890_112,
      5,
      'mock-folder-2-clinical',
      'PDF',
      { previewUrl: PREVIEW_PDF },
    ),
  ],
)

const mockPatient2Lab = createWorkspace(
  'mock-patient-2',
  'mock-folder-2-lab',
  [ROOT_CRUMB, crumb('mock-folder-2-lab', 'Lab results')],
  [],
  [
    mockFile(
      'mock-file-2-lab-blood',
      'Blood_Work_Results.pdf',
      445_440,
      12,
      'mock-folder-2-lab',
      'PDF',
      { previewUrl: PREVIEW_PDF },
    ),
    mockFile(
      'mock-file-2-lab-culture',
      'Culture_Report.pdf',
      654_336,
      12,
      'mock-folder-2-lab',
      'PDF',
      { previewUrl: PREVIEW_PDF },
    ),
  ],
)

const mockFileWorkspaces: Record<string, Record<string, IWorkspace>> = {
  [P1]: {
    root: createWorkspace(P1, null, [ROOT_CRUMB], p1RootFolders, p1RootFiles),
    [P1_XRAY]: p1FolderWorkspace(
      P1_XRAY,
      [ROOT_CRUMB, crumb(P1_XRAY, 'X-Ray Files')],
      p1XrayFiles,
    ),
    [P1_FORMS]: p1FolderWorkspace(
      P1_FORMS,
      [ROOT_CRUMB, crumb(P1_FORMS, 'Forms and Letters')],
      p1FormsFiles,
    ),
    [P1_CLAIMS]: p1FolderWorkspace(
      P1_CLAIMS,
      [ROOT_CRUMB, crumb(P1_CLAIMS, 'Claims')],
      p1ClaimsFiles,
    ),
    [P1_IMAGES]: p1FolderWorkspace(
      P1_IMAGES,
      [ROOT_CRUMB, crumb(P1_IMAGES, 'Images')],
      p1ImagesFiles,
    ),
    [P1_CLINICAL]: p1FolderWorkspace(
      P1_CLINICAL,
      [ROOT_CRUMB, crumb(P1_CLINICAL, 'Clinical records')],
      p1ClinicalFiles,
      [p1Clinical2025Folder],
    ),
    [P1_CLINICAL_2025]: p1FolderWorkspace(
      P1_CLINICAL_2025,
      [
        ROOT_CRUMB,
        crumb(P1_CLINICAL, 'Clinical records'),
        crumb(P1_CLINICAL_2025, '2025 Visits', '/Clinical records/2025 Visits'),
      ],
      p1Clinical2025Files,
    ),
    [P1_INSURANCE]: p1FolderWorkspace(
      P1_INSURANCE,
      [ROOT_CRUMB, crumb(P1_INSURANCE, 'Insurance documents')],
      p1InsuranceFiles,
    ),
  },
  'mock-patient-2': {
    root: mockPatient2Root,
    'mock-folder-2-clinical': mockPatient2Clinical,
    'mock-folder-2-lab': mockPatient2Lab,
  },
}

const allMockWorkspaces = Object.values(mockFileWorkspaces).flatMap(
  (byFolder) => Object.values(byFolder),
)

const findMockFile = (fileId: string): IWorkspaceFileItem | undefined => {
  for (const workspace of allMockWorkspaces) {
    const file = workspace.files.find((f) => f.file_id === fileId)
    if (file) return file
  }
  return undefined
}

const findMockFolder = (
  folderId: string,
): { folder: IWorkspaceFolderItem; patientId: string } | undefined => {
  for (const workspace of allMockWorkspaces) {
    const folder = workspace.folders.find((f) => f.folder_id === folderId)
    if (folder) return { folder, patientId: workspace.scope.patient_id }
  }
  return undefined
}

export const isMockFileId = (fileId?: string | null): boolean =>
  !!fileId?.startsWith('mock-file-')

export const isMockFolderId = (folderId?: string | null): boolean =>
  !!folderId?.startsWith('mock-folder-')

export const getMockWorkspaceSnapshot = (
  patientId: string,
  folderId?: string | null,
): IWorkspace | null => {
  const patientWorkspaces =
    mockFileWorkspaces[patientId] ?? mockFileWorkspaces[P1]
  if (!patientWorkspaces) return null

  const key =
    !folderId || folderId === 'root' || folderId === MOCK_ROOT_FOLDER_ID
      ? 'root'
      : folderId
  const workspace = patientWorkspaces[key] ?? null
  if (!workspace) return null

  if (patientId === workspace.scope.patient_id) {
    return workspace
  }

  return {
    ...workspace,
    scope: { ...workspace.scope, patient_id: patientId },
  }
}

export const getMockFilePreviewUrl = (fileId: string): string | null =>
  mockFileMeta[fileId]?.previewUrl ?? null

export const getMockFileDetails = (fileId: string): IFileDetails | null => {
  const file = findMockFile(fileId)
  if (!file) return null
  const meta = mockFileMeta[fileId] ?? {}
  return {
    file_id: file.file_id,
    name: file.file_name,
    file_name: file.file_name,
    original_name: file.original_name,
    mime_type: file.mime_type,
    file_type: file.file_type,
    file_size: file.file_size,
    upload_date: file.date_time_created_at,
    uploader: meta.uploader ?? FRONT_DESK,
    attached_to_claims: (meta.claimIds?.length ?? 0) > 0,
    claim_ids: meta.claimIds,
    attached_to_forms: !!meta.attachedToForms,
    download_count: file.download_count,
    last_accessed: file.last_accessed,
    folder_id: file.folder_id,
  }
}

export const getMockFolderDetails = (
  folderId: string,
): IFolderDetails | null => {
  const match = findMockFolder(folderId)
  if (!match) return null
  const { folder, patientId } = match
  const files = mockFileWorkspaces[patientId]?.[folderId]?.files ?? []
  const directSize = sumSize(files)
  return {
    folder_id: folder.folder_id,
    folder_name: folder.folder_name,
    folder_type: folder.is_default ? 'default' : 'custom',
    patient_id: patientId,
    parent_folder_id: folder.parent_folder_id,
    aggregate_size_bytes: folder.folder_size,
    direct_file_size_bytes: directSize,
    file_count: files.length,
    total_files: folder.total_files,
    total_folders: folder.total_folders,
    total_items: folder.total_items,
    files: files.map((file) => ({
      file_id: file.file_id,
      file_name: file.file_name,
      original_name: file.original_name,
      file_size: file.file_size,
      file_type: file.file_type,
      mime_type: file.mime_type,
      is_favorite: file.is_favorite,
      uploaded_at: file.date_time_created_at,
    })),
  }
}
